import React, { useState } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useApp } from '../context/AppContext'

const { FiUser, FiLock, FiEye, FiEyeOff, FiBook, FiUserPlus, FiTarget, FiCheck, FiClock, FiTrendingUp, FiBarChart2, FiFilePlus, FiFileText } = FiIcons

const Login = ({ onLogin }) => {
  const { authService } = useApp()
  const [isSignUp, setIsSignUp] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    faculty: '',
    department: '',
    position: '',
    rememberMe: false
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeFeature, setActiveFeature] = useState(0)

  // Features carousel data
  const features = [
    {
      title: "Constructive Alignment",
      description: "Align Course Learning Outcomes (CLOs) with Program Learning Outcomes (PLOs) effortlessly",
      icon: FiTarget
    },
    {
      title: "Performance Analytics",
      description: "Comprehensive grade distribution and achievement analysis with intuitive visualizations",
      icon: FiBarChart2
    },
    {
      title: "Class Group Analysis",
      description: "Track and compare performance across different student groups and classes",
      icon: FiTrendingUp
    },
    {
      title: "Comprehensive Reports",
      description: "Generate detailed reports with student performance metrics and class group breakdowns",
      icon: FiFileText
    },
    {
      title: "MQF Compliance",
      description: "Ensure your courses comply with Malaysian Qualifications Framework requirements",
      icon: FiCheck
    }
  ]

  // Auto-rotate features
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [features.length])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      if (isSignUp) {
        const { user, error } = await authService.signUp(formData.email, formData.password, {
          name: formData.name,
          email: formData.email,
          faculty: formData.faculty,
          department: formData.department,
          position: formData.position
        })

        if (error) throw new Error(error)

        if (user) {
          // Get the profile after signup
          const profileResult = await authService.getProfile(user.id)
          onLogin(user, profileResult.data)
        }
      } else {
        const { user, profile, error } = await authService.signIn(formData.email, formData.password)

        if (error) throw new Error(error)

        if (user) {
          onLogin(user, profile)
        }
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* App Features Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-8 text-white hidden lg:flex lg:flex-col lg:justify-between"
        >
          <div>
            <div className="flex items-center mb-8">
              <div className="bg-white bg-opacity-20 w-12 h-12 rounded-xl flex items-center justify-center mr-4">
                <SafeIcon icon={FiBook} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">CLO-PLO Mapper</h2>
                <p className="text-blue-100">Course Management Excellence</p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold mb-6">Why Choose Our Platform?</h3>
            
            {/* Features Carousel */}
            <div className="relative h-40 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: activeFeature === index ? 1 : 0,
                    y: activeFeature === index ? 0 : 20
                  }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0"
                  style={{ display: activeFeature === index ? 'block' : 'none' }}
                >
                  <div className="bg-white bg-opacity-10 rounded-xl p-6">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-500 bg-opacity-30 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                        <SafeIcon icon={feature.icon} className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="text-lg font-medium">{feature.title}</h4>
                    </div>
                    <p className="text-blue-100">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
              
              {/* Indicator dots */}
              <div className="flex justify-center space-x-2 mt-4">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveFeature(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      activeFeature === index ? 'bg-white' : 'bg-white bg-opacity-30'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="bg-blue-500 bg-opacity-20 p-2 rounded-lg mr-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h5 className="font-medium">Streamlined Course Management</h5>
                  <p className="text-sm text-blue-100">Easily manage course details, learning outcomes, and assessments</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-500 bg-opacity-20 p-2 rounded-lg mr-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h5 className="font-medium">Data-Driven Insights</h5>
                  <p className="text-sm text-blue-100">Visualize student performance and identify improvement areas</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-500 bg-opacity-20 p-2 rounded-lg mr-3">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h5 className="font-medium">Time-Saving Automation</h5>
                  <p className="text-sm text-blue-100">Generate reports and analyze results with a few clicks</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-white border-opacity-20">
            <p className="text-sm text-blue-100">© 2025 FAHIM. All rights reserved.</p>
          </div>
        </motion.div>
        
        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="text-center mb-8">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiBook} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">CLO-PLO Mapper</h1>
            <p className="text-gray-600 mt-2">by FAHIM</p>
          </div>

          <div className="flex mb-6">
            <button
              type="button"
              onClick={() => setIsSignUp(false)}
              className={`flex-1 py-2 px-4 rounded-l-lg transition-colors ${
                !isSignUp
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setIsSignUp(true)}
              className={`flex-1 py-2 px-4 rounded-r-lg transition-colors ${
                isSignUp
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Sign Up
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Faculty
                    </label>
                    <input
                      type="text"
                      name="faculty"
                      value={formData.faculty}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Faculty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Department"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Senior Lecturer"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </div>

            {!isSignUp && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="rememberMe"
                  id="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                  Remember me
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <SafeIcon icon={isSignUp ? FiUserPlus : FiUser} className="w-5 h-5" />
                  <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
                </>
              )}
            </button>
          </form>

          {!isSignUp && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Demo: Use any valid email and password to sign in
              </p>
            </div>
          )}
          
          {/* Mobile Features Section (visible on small screens) */}
          <div className="mt-8 pt-6 border-t border-gray-200 lg:hidden">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Key Features</h3>
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <SafeIcon icon={feature.icon} className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800">{feature.title}</h5>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Login